/**
 * The paperwork a line's ERP jobs produce — the Certificate of Conformity and
 * Despatch Note (generated) and the FAIR reports the ERP holds (copied files).
 *
 * Given a tab of its own rather than sitting under Job Status, where it read
 * as part of the progress table it was pinned above. These are the documents
 * that travel with the goods; people come looking for them by name.
 *
 * Only reached when the line has imported jobs — the modal hides the tab
 * otherwise, since both documents are built from job records.
 */
import { FileBadge, FileText } from 'lucide-react';
import JobDocumentButtons from './JobDocumentButtons';
import ERPDocumentList from './ERPDocumentList';

interface POLineDocumentsTabProps {
  /** PO line whose jobs the documents are generated from. */
  lineId: number;
}

/** One document explained, so the pair is not two unlabelled buttons. */
function DocumentNote({ icon: Icon, title, children }: {
  icon: typeof FileBadge;
  title: string;
  children: React.ReactNode;
}) {
  return (
    <div className="flex gap-3">
      <Icon className="h-4 w-4 text-muted-foreground shrink-0 mt-0.5" aria-hidden="true" />
      <div className="min-w-0">
        <div className="text-sm font-medium">{title}</div>
        <p className="text-xs text-muted-foreground mt-0.5">{children}</p>
      </div>
    </div>
  );
}

/**
 * Documents tab for a PO line.
 *
 * @param lineId - the line whose jobs the documents cover
 */
export default function POLineDocumentsTab({ lineId }: POLineDocumentsTabProps) {
  return (
    <div className="space-y-5">
      <JobDocumentButtons lineId={lineId} />

      <div className="grid gap-4 sm:grid-cols-2 border-t pt-4">
        <DocumentNote icon={FileBadge} title="Certificate of Conformity">
          Certifies the parts against every process the route put them through,
          each with the specification and issue it was processed to.
        </DocumentNote>
        <DocumentNote icon={FileText} title="Despatch Note">
          The note that travels with the goods, covering what was despatched
          against this line.
        </DocumentNote>
      </div>

      <p className="text-xs text-muted-foreground">
        Both are generated from the ERP job records for this line and open in a
        new tab.
      </p>

      <ERPDocumentList lineId={lineId} />
    </div>
  );
}
